/**
 * llmInputEvidence
 * ----------------
 * Reading the evidence an AI paragraph was written from (RmsDocumentReview's
 * "What this paragraph was written from" panel).
 *
 * The backend hands several of these inputs over as ONE multi-line string —
 * the surfaced form evidence is one line per answer, each prefixed with the
 * factor it belongs to (rms_llm_inputs._format_surfaced_factors):
 *
 *   Hearing or vision — When was their last eye test? (Parent/carer): Within the last year
 *
 * Rendered as a single block that is a wall of text: forty answers run
 * together, and a clinician checking what the AI was given has to read every
 * line to find one (Eve, 27 October 2026). These helpers turn the block back
 * into the structure it already has — grouped by factor, one answer at a
 * time, each with the respondent who gave it — so the panel can lay it out.
 *
 * Nothing is ever dropped: a line that does not match the expected shape is
 * carried through whole, and a value with no line breaks is left alone.
 */

import { splitEvidenceLines } from './evidenceOverrides';

/** `Question text (Respondent): answer` → its three parts, or null. */
function parseAnswerLine(text) {
  const match = /^(.*?)\s*\(([^()]+)\):\s*([\s\S]*)$/.exec(text);
  if (!match) return null;
  const [, question, respondent, answer] = match;
  if (!question.trim() || !answer.trim()) return null;
  return { question: question.trim(), respondent: respondent.trim(), answer: answer.trim() };
}

/**
 * Parse a multi-line evidence value into factor groups.
 *
 * @param {string} value - the raw field value
 * @returns {Array<{ factor: string|null, items: Array<{
 *   question: string|null, respondent: string|null, answer: string, raw: string }> }>}
 *   groups in their original order, or an empty array when there is nothing
 *   to lay out (a single line, or an empty value — the caller then renders
 *   the value as it always did).
 */
export function parseEvidenceLines(value) {
  const text = typeof value === 'string' ? value : '';
  const lines = text.split('\n').map((line) => line.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  return groupEvidenceLines(lines.map((line) => ({ text: line })));
}

/**
 * Group already-split lines by factor, as {@link parseEvidenceLines} does,
 * keeping each line's own `index` (its position in the stored field, which
 * the TDI-406 evidence controls key on) and any other properties it carries.
 *
 * @param {Array<{index: number, text: string}>} lineEntries
 * @returns {Array<{ factor: string|null, items: Array<object> }>}
 */
export function groupEvidenceLines(lineEntries) {
  const groups = [];
  (lineEntries || []).forEach((entry) => {
    const { text: line, ...extra } = entry;
    // The factor prefix is separated by an em dash; a question may itself
    // contain dashes, so only the FIRST separator counts.
    const split = line.split(' — ');
    const hasFactor = split.length > 1;
    const factor = hasFactor ? split[0].trim() : null;
    const rest = hasFactor ? split.slice(1).join(' — ').trim() : line;

    const parsed = parseAnswerLine(rest);
    const item = parsed
      ? { ...extra, ...parsed, raw: line }
      : { ...extra, question: null, respondent: null, answer: rest, raw: line };

    const last = groups[groups.length - 1];
    if (last && last.factor === factor) {
      last.items.push(item);
    } else {
      groups.push({ factor, items: [item] });
    }
  });
  return groups;
}

// ─────────────────────────────────────────────────────────────────────────────
// Who each input came from (TDI-698, R-017)
// ─────────────────────────────────────────────────────────────────────────────
// A structured outcome letter's paragraph inputs arrive in three categories
// (rms_llm_inputs._outcome_letter_inputs): what the clinician recorded, what
// the school said and what the parent or carer said, each field naming its
// role. The panel shows them in that fixed order, never the stored map's
// order, with every line tagged by who it came from.

export const INPUT_GROUP_DECISIONS = 'Your decisions';
export const INPUT_GROUP_SCHOOL = 'School provided';
export const INPUT_GROUP_PARENT = 'Parent or carer provided';
export const INFORMANT_GROUP_HEADING = 'Informant provided information';

/** The tag on each line, by the role it came from. */
export const INPUT_ROLE_TAGS = {
  clinician: 'Your decision',
  senco: 'SENCO',
  teacher: 'Teacher',
  guardian: 'Parent or carer',
  // A school answer whose submitter cannot be told apart (older records,
  // legacy SNAP-IV instance, demo data).
  school: 'School',
};

const GROUP_KEYS = {
  [INPUT_GROUP_DECISIONS]: 'decisions',
  [INPUT_GROUP_SCHOOL]: 'school',
  [INPUT_GROUP_PARENT]: 'parent',
};
const GROUP_DEFAULT_ROLES = { decisions: 'clinician', school: 'school', parent: 'guardian' };

// The order of the clinician's own fields within "Your decisions", whatever
// order the stored map comes back in: what they rated, then what they
// tagged, then the decision, conclusion and signposting.
const DECISION_FIELD_ORDER = [
  'evidence_answers', 'other_factors', 'evidence_tags', 'outcome', 'conclusion',
  'signposting', 'signposting_selections',
];

function fieldRank(groupKey, fieldKey) {
  if (groupKey !== 'decisions') return fieldKey === 'snap_summary' ? 0 : 1;
  const base = String(fieldKey).startsWith('evidence_tags') ? 'evidence_tags' : fieldKey;
  const rank = DECISION_FIELD_ORDER.indexOf(base);
  return rank === -1 ? DECISION_FIELD_ORDER.length : rank;
}

// Letters drafted before TDI-698 stored their inputs by data type. Their
// categories are mapped onto the three groups on display: the captured form
// answers and SNAP-IV lines by the respondent each line names, everything
// else under "Your decisions".
const LEGACY_TAG_SOURCE_ROLES = { 'home/parent': 'guardian', school: 'school' };

function legacyRespondentRole(line) {
  const split = line.split(' — ');
  const rest = split.length > 1 ? split.slice(1).join(' — ') : line;
  const parsed = parseAnswerLine(rest);
  if (!parsed) return null;
  if (/school/i.test(parsed.respondent)) return 'school';
  if (/parent|carer/i.test(parsed.respondent)) return 'guardian';
  return null;
}

function legacyLinePlacement(category, line) {
  if (category === 'Captured Form Evidence') {
    const role = legacyRespondentRole(line);
    return role === 'school' ? { group: 'school', role } : { group: 'parent', role: role || 'guardian' };
  }
  if (category === 'SNAP-IV Summary') {
    return /^parent/i.test(line)
      ? { group: 'parent', role: 'guardian' }
      : { group: 'school', role: 'school' };
  }
  if (category === 'Clinician-Tagged Evidence') {
    const match = /\(source: ([^)]+)\)/.exec(line);
    const answerFrom = match ? LEGACY_TAG_SOURCE_ROLES[match[1].trim()] || null : null;
    return { group: 'decisions', role: 'clinician', answerFrom };
  }
  return { group: 'decisions', role: 'clinician' };
}

/**
 * Lay out one paragraph's stored inputs as the panel shows them.
 *
 * @param {object} paragraphInputs - { category: { fieldKey: { value, source, role?, answerFrom? } } }
 * @returns {{ decisions: Array<Entry>, school: Array<Entry>, parent: Array<Entry> }}
 *   where Entry = { category, fieldKey, source, lines: Array<{ index, text, role, answerFrom }> }.
 *   `category` and `fieldKey` are the stored ones and `index` the line's
 *   position in the stored field, so the TDI-406 controls keep their keys.
 *   Empty fields are left out; a legacy field whose lines belong to two
 *   groups appears in each, with only its own lines.
 */
export function groupLetterInputs(paragraphInputs) {
  const out = { decisions: [], school: [], parent: [] };
  if (!paragraphInputs || typeof paragraphInputs !== 'object') return out;

  Object.entries(paragraphInputs).forEach(([category, fields]) => {
    if (category === 'Question that LLM answered') return;
    if (!fields || typeof fields !== 'object') return;
    const groupKey = GROUP_KEYS[category] || null;

    Object.entries(fields).forEach(([fieldKey, field]) => {
      const value = field && typeof field === 'object' ? field.value : field;
      const source = field && typeof field === 'object' ? field.source || null : null;
      const lines = splitEvidenceLines(typeof value === 'string' ? value : String(value ?? ''));
      if (!lines.length) return;

      const byGroup = {};
      lines.forEach((text, index) => {
        let placement;
        if (groupKey) {
          placement = {
            group: groupKey,
            role: (field && field.role) || GROUP_DEFAULT_ROLES[groupKey],
            answerFrom: (field && field.answerFrom) || null,
          };
        } else {
          placement = legacyLinePlacement(category, text);
        }
        if (!byGroup[placement.group]) {
          byGroup[placement.group] = { category, fieldKey, source, lines: [] };
        }
        byGroup[placement.group].lines.push({
          index, text, role: placement.role || null, answerFrom: placement.answerFrom || null,
        });
      });
      Object.entries(byGroup).forEach(([group, entry]) => out[group].push(entry));
    });
  });

  Object.keys(out).forEach((group) => {
    out[group] = out[group]
      .map((entry, position) => ({ entry, position }))
      .sort((a, b) => (fieldRank(group, a.entry.fieldKey) - fieldRank(group, b.entry.fieldKey))
        || (a.position - b.position))
      .map(({ entry }) => entry);
  });
  return out;
}
